import { useState } from 'react';
import { fa, toman } from './fa';
import type { PendingAttempt } from './terminal';

/*
 * Settling an ambiguous card payment.
 *
 * When the reader's answer is lost, the till records nothing and locks the
 * basket: the customer may already have been charged, and charging again is
 * the one outcome that must never happen by accident. That lock was correct
 * and complete — except that nothing in the till could lift it. The payment
 * screen told a cashier to «از سرشیفت بخواهید وضعیت این فاکتور را مشخص کند»
 * and then offered no way for the سرشیفت to say anything, so a held payment
 * blocked its basket until somebody with a terminal session called the API by
 * hand. One sat unresolved for a day and blocked a counter mid-service.
 *
 * This is that missing control, and it is deliberately not a convenient one.
 */

const FIELD: React.CSSProperties = {
  height: 46, padding: '0 14px', borderRadius: 10, fontSize: 15,
  border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
  color: 'var(--luma-ink)', width: '100%',
};

function Attempt({
  attempt, onResolve, busy,
}: {
  attempt: PendingAttempt;
  onResolve: (invoiceId: string, resolution: 'charged' | 'not_charged', by: string) => Promise<void>;
  busy: boolean;
}) {
  const [by, setBy] = useState('');
  /*
   * "Charged" takes a second press.
   *
   * The two verdicts are not equally dangerous. «کسر نشده» frees the basket to
   * be charged again, and if that is wrong the error shows up immediately —
   * the reader refuses, or the customer says they already paid. «کسر شده»
   * records money the shop may never have received and closes the matter; the
   * error surfaces at cash-up, days later, with nothing left to check it
   * against. So the destructive one is the one that asks twice.
   */
  const [confirming, setConfirming] = useState(false);
  const named = by.trim().length > 0;

  const waited = Math.max(0, Math.round((Date.now() - new Date(attempt.startedAt).getTime()) / 60000));

  return (
    <div
      style={{
        border: '1px solid var(--luma-tint-danger-border)',
        background: 'var(--luma-tint-danger-bg)',
        borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <div className="pos-num" style={{ fontSize: 26, fontWeight: 800, color: 'var(--luma-tint-danger-text)' }}>
          {toman(attempt.amountRial)}
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--luma-muted)' }}>
          {waited > 0 ? `${fa(waited)} دقیقه پیش` : 'همین حالا'}
        </div>
      </div>

      <div style={{ fontSize: 13.5, lineHeight: 1.9, color: 'var(--luma-ink-2)' }}>
        رسید دستگاه کارت‌خوان را ببینید. تنها رسید دستگاه مشخص می‌کند مبلغ از مشتری کسر شده یا نه —
        حدس زدن اینجا یعنی یا دوباره از مشتری پول گرفتن، یا فروشی که پولش هرگز نرسیده.
      </div>

      {attempt.rrn ? (
        <div style={{ fontSize: 13 }}>
          شماره پیگیری دستگاه: <span className="pos-num">{fa(attempt.rrn)}</span>
        </div>
      ) : null}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
        نام تأییدکننده
        <input
          value={by}
          onChange={(e) => { setBy(e.target.value); setConfirming(false); }}
          placeholder="نام سرشیفت"
          style={FIELD}
        />
      </label>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          disabled={!named || busy}
          onClick={() => onResolve(attempt.invoiceId, 'not_charged', by.trim())}
          style={{
            height: 52, padding: '0 20px', borderRadius: 12, fontWeight: 700, fontSize: 14.5,
            border: '1px solid var(--luma-line-strong)', background: 'var(--luma-card)',
            opacity: !named || busy ? 0.5 : 1,
          }}
        >
          مبلغ کسر نشده
        </button>

        <button
          disabled={!named || busy}
          onClick={() => {
            if (!confirming) { setConfirming(true); return; }
            void onResolve(attempt.invoiceId, 'charged', by.trim());
          }}
          style={{
            height: 52, padding: '0 20px', borderRadius: 12, fontWeight: 800, fontSize: 14.5,
            border: `1px solid ${confirming ? 'var(--luma-tint-danger-border)' : 'var(--luma-line-strong)'}`,
            background: confirming ? 'var(--luma-tint-danger-bg)' : 'var(--luma-card)',
            color: confirming ? 'var(--luma-tint-danger-text)' : 'var(--luma-ink)',
            opacity: !named || busy ? 0.5 : 1,
          }}
        >
          {confirming ? 'مطمئنم — مبلغ کسر شده' : 'مبلغ کسر شده'}
        </button>
      </div>

      {!named && (
        <div style={{ fontSize: 12.5, color: 'var(--luma-muted)' }}>
          بدون نام تأییدکننده ثبت نمی‌شود — این تصمیم باید صاحب داشته باشد.
        </div>
      )}
    </div>
  );
}

export function ResolveHeldPayment({
  attempts, onResolve, error, busy,
}: {
  attempts: PendingAttempt[];
  onResolve: (invoiceId: string, resolution: 'charged' | 'not_charged', by: string) => Promise<void>;
  error: string | null;
  busy: boolean;
}) {
  if (attempts.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontWeight: 800, fontSize: 17 }}>
        {attempts.length > 1
          ? `${fa(attempts.length)} پرداخت نامشخص`
          : 'پرداخت نامشخص'}
      </div>
      {error && (
        <div style={{ fontSize: 13.5, color: 'var(--luma-tint-danger-text)' }}>{error}</div>
      )}
      {attempts.map((a) => (
        <Attempt key={a.invoiceId} attempt={a} onResolve={onResolve} busy={busy} />
      ))}
    </div>
  );
}
